import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GEONORGE_MUNICIPALITIES, GEONORGE_COUNTIES_WITH_MUNICIPALITIES } from 'src/constants/hook-keys';
import { Municipalities2023Map } from '../constants/municipalities2023';

export type Municipality = {
  number: string;
  name: string;
};

export type CountyWithMunicipalities = {
  fylkesnavn: string;
  fylkesnummer: string;
  kommuner: Municipalities[];
};

export type Municipalities = {
  kommunenavnNorsk: string;
  kommunenummer: string;
};

const useGeonorge = () => {
  const countiesWithMunicipalitiesQuery = useQuery({
    queryKey: [GEONORGE_COUNTIES_WITH_MUNICIPALITIES],
    queryFn: () =>
      fetch(
        'https://api.kartverket.no/kommuneinfo/v1/fylkerkommuner?filtrer=fylkesnavn%2Cfylkesnummer%2Ckommuner.kommunenavnNorsk%2Ckommuner.kommunenummer',
      ).then((data) => data.json()),
    retry: 1,
    staleTime: 30 * 24 * 60 * 60 * 1000,
  });

  const municipalityQuery = useQuery({
    queryKey: [GEONORGE_MUNICIPALITIES],
    queryFn: () => fetch('https://ws.geonorge.no/kommuneinfo/v1/kommuner').then((data) => data.json()),
    retry: 1,
    staleTime: 30 * 24 * 60 * 60 * 1000,
  });

  const {
    municipalities,
    municipalityMap,
  }: { municipalities: Municipality[]; municipalityMap: Map<string, string> } = useMemo(() => {
    if (!municipalityQuery.data) {
      return { municipalities: [] as Municipality[], municipalityMap: new Map<string, string>() };
    }

    const municipalities = municipalityQuery.data.map((x) => ({
      number: x.kommunenummer,
      name: x.kommunenavnNorsk,
    }));

    return {
      municipalities,
      municipalityMap: new Map<string, string>(
        municipalities.map((municipality) => [municipality.number, municipality.name]),
      ),
    };
  }, [municipalityQuery.data]);

  const countiesWithMunicipalities: CountyWithMunicipalities[] = useMemo(() => {
    if (!countiesWithMunicipalitiesQuery.data) {
      return [];
    }
    return countiesWithMunicipalitiesQuery.data;
  }, [countiesWithMunicipalitiesQuery.data]);

  const getMunicipalityNameByNumber = useCallback(
    (number: string | undefined) => {
      if (number == undefined) return null;
      let name = municipalityMap.get(number);
      if (!name) {
        // Check if municipality name is in the 2023 municipality list
        name = Municipalities2023Map.get(number);
      }

      return name;
    },
    [municipalityMap],
  );

  const getMunicipalityNumberByName = useCallback(
    (name: string | undefined) => {
      if (name == undefined) return null;
      const number = municipalities.find(
        (municipality) => municipality.name.toLowerCase() === name.toLowerCase(),
      )?.number;

      return number;
    },
    [municipalities],
  );

  return {
    municipalities,
    countiesWithMunicipalities,
    getMunicipalityNameByNumber,
    getMunicipalityNumberByName,
  };
};

export default useGeonorge;
